import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { FinanceDocumentsPanel } from "../admin/shipment-detail/FinanceDocumentsPanel";
import StatusPill from "../../components/StatusPill";
import { fmtINR } from "../../lib/constants";
import { toast } from "sonner";
import { ArrowLeft, FileArrowUp, ArrowsClockwise, Eye, PencilSimple, WarningCircle, CheckCircle } from "@phosphor-icons/react";

const STAGES = [
  { key: "source_road", label: "Source Road", prereq: (w) => (w.vehicles?.source_vehicle_numbers?.length ?? 0) > 0,
    prereqMsg: "Awaiting Source vehicle assignment" },
  { key: "air_leg", label: "Air Leg", prereq: (w) => !!w.awb?.finalized,
    prereqMsg: "Awaiting AWB number" },
  { key: "destination_road", label: "Destination Road", prereq: (w) => (w.vehicles?.destination_vehicle_numbers?.length ?? 0) > 0,
    prereqMsg: "Awaiting Destination vehicle assignment" },
];

// M10A FE2 — restricted Accounts workspace. Consumes ONLY FE1 GET /accounts/shipments/{id} +
// the dedicated Inc-5 EWB APIs + the existing secure signed-document route for INV/AWB + the
// finance-documents API (A4, assigned Accounts). It NEVER calls the broad admin shipment-detail endpoint.
export default function AccountsWorkspace() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const [w, setW] = useState(null);   // null = loading
  const [gone, setGone] = useState(false);

  const handleAccessError = useCallback((e) => {
    const code = e?.response?.status;
    if (code === 403 || code === 404) {
      setGone(true);
      toast.error("This shipment is no longer assigned to you.");
      navigate("/accounts", { replace: true });
      return true;
    }
    return false;
  }, [navigate]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/accounts/shipments/${id}`);
      setW(data);
    } catch (e) {
      if (!handleAccessError(e)) toast.error(e.displayMessage || "Failed to load");
    }
  }, [id, handleAccessError]);

  useEffect(() => { load(); }, [load]);

  const openSigned = async (url, label) => {
    try {
      const { data } = await api.get(url);
      if (data?.signed_url) window.open(data.signed_url, "_blank", "noopener");
      else toast.error(`No ${label} available`);
    } catch (e) { if (!handleAccessError(e)) toast.error(e.displayMessage || "Failed to open"); }
  };

  if (gone) return null;
  if (w === null) return <div className="p-10 mono text-sm" data-testid="accounts-workspace-loading">Loading…</div>;
  if (w.finance_only) return <FinanceOnlyWorkspace sh={w.shipment || {}} id={id} profile={profile} onBack={() => navigate("/accounts")} />;

  const sh = w.shipment || {};
  const p = w.parties || {};
  const c = w.commercial || {};
  const shipCancelled = sh.status === "CANCELLED";

  return (
    <div className="max-w-3xl mx-auto p-4 sm:p-6" data-testid="accounts-workspace">
      <button onClick={() => navigate("/accounts")} className="text-xs mono text-stone-500 underline inline-flex items-center gap-1 mb-3" data-testid="accounts-back">
        <ArrowLeft size={13} weight="bold" /> Assigned Shipments
      </button>

      {/* SHIPMENT */}
      <header className="mb-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h1 className="text-2xl mono font-medium" data-testid="ws-code">{sh.shipment_code}</h1>
          <StatusPill status={sh.status} data-testid="ws-status" />
        </div>
        <div className="text-xs mono text-stone-500 mt-1" data-testid="ws-route">
          {sh.route?.source_airport} → {sh.route?.destination_airport}
        </div>
        <div className="mt-2">
          {sh.accounts_action_required
            ? <span className="inline-flex items-center gap-1 text-xs mono text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-0.5" data-testid="ws-action-required"><WarningCircle size={13} weight="bold" /> Action Required</span>
            : <span className="inline-flex items-center gap-1 text-xs mono text-stone-400" data-testid="ws-no-action"><CheckCircle size={13} weight="bold" /> No action required</span>}
        </div>
      </header>

      {/* REFUND (cancelled shipment — finance carve-out only) */}
      {w.refund && <RefundPanel refund={w.refund} shipmentId={id} reload={load} handleAccessError={handleAccessError} />}

      {/* FINANCE DOCUMENTS (assigned Accounts; renders nothing when none exist) */}
      <div className="mb-4" data-testid="ws-finance-documents">
        <FinanceDocumentsPanel d={{ id }} profile={profile} />
      </div>

      {/* PARTIES + COMMERCIAL */}
      <div className="grid sm:grid-cols-2 gap-3 mb-4">
        <Card title="Parties" testid="ws-parties">
          <Row k="Sender / Company" v={p.sender_company_name} />
          <Row k="Receiver" v={p.receiver_name} />
          <Row k="Sender address" v={fmtAddr(p.sender_address)} />
          <Row k="Receiver address" v={fmtAddr(p.receiver_address)} />
        </Card>
        <Card title="Commercial (read-only)" testid="ws-commercial">
          <Row k="MAIN payable" v={fmtINR(c.main_payable_amount)} />
          <Row k="MAIN payment status" v={c.main_payment_status} />
          <Row k="Declared invoice value" v={fmtINR(c.declared_invoice_value)} />
        </Card>
      </div>

      {/* VEHICLES + AWB */}
      <div className="grid sm:grid-cols-2 gap-3 mb-4">
        <Card title="Vehicles (reference)" testid="ws-vehicles">
          <Row k="Source vehicles" v={(w.vehicles?.source_vehicle_numbers || []).join(", ") || w.vehicles?.source_vehicle_number} />
          <Row k="Destination vehicles" v={(w.vehicles?.destination_vehicle_numbers || []).join(", ") || w.vehicles?.destination_vehicle_number} />
        </Card>
        <Card title="AWB" testid="ws-awb">
          {(w.awb?.awbs || []).length > 0 ? (
            <div className="space-y-0.5" data-testid="ws-awb-list">
              {w.awb.awbs.map((a) => (
                <Row key={a.id} k={`AWB ${a.awb_number || "(pending)"}`} v={a.awb_chargeable_weight_kg != null ? `${a.awb_chargeable_weight_kg} kg` : "—"} />
              ))}
              <Row k="Total Chargeable Weight" v={w.awb?.aggregate_chargeable_weight_kg != null ? `${w.awb.aggregate_chargeable_weight_kg} kg` : "—"} />
            </div>
          ) : <Row k="AWBs" v="None yet" />}
          {!shipCancelled && ((w.awb?.documents || []).length > 0 ? (
            <div className="flex flex-wrap gap-2 mt-1">
              {w.awb.documents.map((d) => (
                <button key={d.document_id} className="btn-secondary text-xs inline-flex items-center gap-1"
                        onClick={() => openSigned(`/shipments/${id}/documents/${d.document_id}/signed-url`, "AWB")}
                        data-testid={`ws-awb-view-${d.document_id}`}>
                  <Eye size={13} weight="bold" /> View AWB
                </button>
              ))}
            </div>
          ) : <div className="text-xs mono text-stone-400">No AWB image yet</div>)}
        </Card>
      </div>

      {/* INVOICES + EWB */}
      <div className="mb-2 overline">Invoices &amp; E-Way Bills</div>
      {(w.invoices || []).length === 0 ? (
        <div className="text-sm mono text-stone-500 py-6 text-center border border-dashed border-stone-200 rounded" data-testid="ws-no-invoices">
          No current invoices uploaded yet.
        </div>
      ) : (
        <div className="space-y-4" data-testid="ws-invoice-list">
          {w.invoices.map((inv) => (
            <InvoiceCard key={inv.document_id} inv={inv} w={w} shipmentId={id} shipCancelled={shipCancelled}
                         openSigned={openSigned} reload={load} handleAccessError={handleAccessError} />
          ))}
        </div>
      )}
    </div>
  );
}

function FinanceOnlyWorkspace({ sh, id, profile, onBack }) {
  return (
    <div className="max-w-3xl mx-auto p-4 sm:p-6" data-testid="accounts-workspace-finance-only">
      <button onClick={onBack} className="text-xs mono text-stone-500 underline inline-flex items-center gap-1 mb-3" data-testid="accounts-back">
        <ArrowLeft size={13} weight="bold" /> Assigned Shipments
      </button>
      <header className="mb-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h1 className="text-2xl mono font-medium" data-testid="ws-code">{sh.shipment_code}</h1>
          <StatusPill status={sh.status} data-testid="ws-status" />
        </div>
        <div className="text-xs mono text-stone-500 mt-1" data-testid="ws-route">
          {sh.route?.source_airport} → {sh.route?.destination_airport}
        </div>
      </header>
      <div className="mb-4" data-testid="ws-finance-documents">
        <FinanceDocumentsPanel d={{ id }} profile={profile} />
      </div>
    </div>
  );
}

// P2-12: CANCELLED shipments omit all operational/document-view actions (backend terminal guards reject them).
function InvoiceCard({ inv, w, shipmentId, shipCancelled, openSigned, reload, handleAccessError }) {
  const ewb = inv.eway_bill || {};
  const fileInputs = useRef({});
  const [numberEdit, setNumberEdit] = useState(false);
  const [numberVal, setNumberVal] = useState(ewb.ewb_number || "");
  const [busy, setBusy] = useState(false);

  const runAction = async (fn) => {
    setBusy(true);
    try { await fn(); }
    catch (e) { if (!handleAccessError(e)) toast.error(e.displayMessage || "Action failed"); }
    finally { setBusy(false); }
  };

  const uploadStage = (stageKey, isReplace) => async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (isReplace && !window.confirm("Replace the current Air12 E-Way Bill copy for this stage? The existing Air12 copy will be superseded.")) return;
    await runAction(async () => {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/shipments/${shipmentId}/invoices/${inv.document_id}/eway-bill/stage/${stageKey}`, fd);
      if (data?.established) toast.success("E-Way Bill created and document uploaded");
      else toast.success("E-Way Bill document uploaded");
      await reload();
    });
  };

  const saveNumber = () => runAction(async () => {
    await api.patch(`/shipments/${shipmentId}/eway-bills/${ewb.id}/number`, { ewb_number: numberVal });
    toast.success("E-Way Bill number saved");
    setNumberEdit(false);
    await reload();
  });

  const confirmMatch = () => runAction(async () => {
    if (!window.confirm("Confirm the replacement invoice as the current invoice relationship? Accounts has checked the replacement against the external E-Way Bill workflow.")) return;
    await api.post(`/shipments/${shipmentId}/eway-bills/${ewb.id}/confirm-match`, {});
    toast.success("Replacement confirmed as current invoice");
    await reload();
  });

  return (
    <div className="border border-stone-200 rounded-lg p-4" data-testid={`ws-invoice-${inv.public_document_id}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="mono text-sm font-medium">{inv.file_name || inv.public_document_id}</div>
        {!shipCancelled && (
          <button className="btn-secondary text-xs inline-flex items-center gap-1"
                  onClick={() => openSigned(`/shipments/${shipmentId}/documents/${inv.document_id}/signed-url`, "invoice")}
                  data-testid={`ws-invoice-view-${inv.public_document_id}`}>
            <Eye size={13} weight="bold" /> View Invoice
          </button>
        )}
      </div>

      {/* EWB number */}
      <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid={`ws-ewb-number-${inv.public_document_id}`}>
        <span className="text-xs mono text-stone-500">EWB No.</span>
        {shipCancelled || !numberEdit ? (
          <>
            <span className="mono text-sm">{ewb.ewb_number || <span className="text-stone-400">— not set —</span>}</span>
            {!shipCancelled && (
              <button className="btn-secondary text-xs inline-flex items-center gap-1" disabled={!ewb.exists || busy}
                      onClick={() => { setNumberVal(ewb.ewb_number || ""); setNumberEdit(true); }}
                      data-testid={`ws-ewb-number-edit-${inv.public_document_id}`}>
                <PencilSimple size={12} weight="bold" /> Edit
              </button>
            )}
          </>
        ) : (
          <>
            <input className="input-field text-sm" value={numberVal} onChange={(e) => setNumberVal(e.target.value)}
                   data-testid={`ws-ewb-number-input-${inv.public_document_id}`} />
            <button className="btn-primary text-xs" disabled={busy || !numberVal.trim()} onClick={saveNumber}
                    data-testid={`ws-ewb-number-save-${inv.public_document_id}`}>Save</button>
            <button className="btn-secondary text-xs" disabled={busy} onClick={() => setNumberEdit(false)}>Cancel</button>
          </>
        )}
      </div>
      {!ewb.exists && !shipCancelled && (
        <div className="text-xs mono text-stone-400 mt-1">
          No E-Way Bill yet — upload the first applicable stage document below to create it.
        </div>
      )}

      {/* needs accounts check / pending replacement */}
      {ewb.needs_accounts_check && !shipCancelled && (
        <div className="mt-3 text-xs mono text-amber-700 bg-amber-50 border border-amber-200 rounded p-2"
             data-testid={`ws-needs-check-${inv.public_document_id}`}>
          Invoice replacement awaiting Accounts check.
          {ewb.pending_replacement && " A pending replacement invoice is associated with this relationship."}
          <div className="mt-2">
            <button className="btn-primary text-xs" disabled={busy} onClick={confirmMatch}
                    data-testid={`ws-confirm-match-${inv.public_document_id}`}>Confirm Match</button>
          </div>
        </div>
      )}

      {/* stage slots */}
      <div className="mt-3 grid sm:grid-cols-3 gap-2">
        {STAGES.map((st) => {
          const present = ewb.stages?.[st.key]?.present;
          const actionable = st.prereq(w);
          return (
            <div key={st.key} className="border border-stone-100 rounded p-2" data-testid={`ws-stage-${st.key}-${inv.public_document_id}`}>
              <div className="text-xs mono font-medium">{st.label}</div>
              <div className="text-xs mono text-stone-500 mt-0.5">
                {present ? "Document present" : "No document"}
              </div>
              {!shipCancelled && <div className="flex flex-wrap gap-1 mt-1">
                {present && ewb.exists && (
                  <button className="btn-secondary text-xs inline-flex items-center gap-1"
                          onClick={() => openSigned(`/shipments/${shipmentId}/eway-bills/${ewb.id}/stage/${st.key}/signed-url`, "E-Way Bill PDF")}
                          data-testid={`ws-stage-view-${st.key}-${inv.public_document_id}`}>
                    <Eye size={12} weight="bold" /> View
                  </button>
                )}
                {actionable ? (
                  <>
                    <input type="file" accept="application/pdf,.pdf" className="hidden"
                           ref={(el) => (fileInputs.current[st.key] = el)}
                           onChange={uploadStage(st.key, present)}
                           data-testid={`ws-stage-file-${st.key}-${inv.public_document_id}`} />
                    <button className="btn-primary text-xs inline-flex items-center gap-1" disabled={busy}
                            onClick={() => fileInputs.current[st.key]?.click()}
                            data-testid={`ws-stage-${present ? "replace" : "upload"}-${st.key}-${inv.public_document_id}`}>
                      {present ? <><ArrowsClockwise size={12} weight="bold" /> Replace</> : <><FileArrowUp size={12} weight="bold" /> Upload</>}
                    </button>
                  </>
                ) : (
                  <span className="text-[11px] mono text-stone-400">{st.prereqMsg}</span>
                )}
              </div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function RefundPanel({ refund, shipmentId, reload, handleAccessError }) {
  const [busy, setBusy] = useState(false);
  const completed = refund.status === "REFUND_COMPLETED";

  const process = async () => {
    if (!window.confirm(`Confirm the refund of ${fmtINR(refund.refund_amount)} has been processed (money moved to the customer)? This cannot be undone.`)) return;
    setBusy(true);
    try {
      // One-click — no UTR/reference/note/amount; refund amount is server-frozen.
      await api.post(`/booking/${shipmentId}/cancellation/refund-complete`, {});
      toast.success("Refund processed");
      await reload();
    } catch (e) {
      if (!handleAccessError(e)) toast.error(e.displayMessage || "Could not complete the refund.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-stone-300 rounded-lg p-4 mb-4 bg-stone-50" data-testid="accounts-refund-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="overline">Refund settlement</div>
        <span className={`text-xs mono px-2 py-0.5 rounded border ${completed ? "text-emerald-700 bg-emerald-50 border-emerald-200" : "text-amber-700 bg-amber-50 border-amber-200"}`}
              data-testid="accounts-refund-status">
          {completed ? "Refund completed" : "Refund pending"}
        </span>
      </div>
      <div className="flex items-center justify-between mt-3">
        <span className="text-sm mono text-stone-500">Refund amount</span>
        <span className="text-lg mono font-medium" data-testid="accounts-refund-amount">{fmtINR(refund.refund_amount)}</span>
      </div>
      {completed && refund.refund_reference && (
        <div className="text-xs mono text-stone-500 mt-1" data-testid="accounts-refund-reference">
          Reference: {refund.refund_reference}
        </div>
      )}
      {refund.can_process && (
        <button className="btn-primary text-sm mt-3" disabled={busy} onClick={process}
                data-testid="accounts-refund-processed-button">
          {busy ? "Processing…" : "Refund processed"}
        </button>
      )}
    </div>
  );
}

function Card({ title, testid, children }) {
  return (
    <div className="border border-stone-200 rounded-lg p-3" data-testid={testid}>
      <div className="overline mb-2">{title}</div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between gap-3 text-sm">
      <span className="text-xs mono text-stone-500">{k}</span>
      <span className="mono text-right">{v || <span className="text-stone-400">—</span>}</span>
    </div>
  );
}

function fmtAddr(a) {
  if (!a) return null;
  if (typeof a === "string") return a;
  return [a.line1, a.line2, a.city, a.state, a.pincode].filter(Boolean).join(", ") || null;
}
